import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {
    downloadPaymentAdvices,
    fetchPaymentAdvices,
    getPaymentAdviceDetails,
    getPaymentAdviceFilters,
    PaymentAdviceDetails,
    PaymentAdviceFiltersResponse,
    PaymentAdvicesRequest,
    PaymentAdviceSummary,
    retryPaymentAdviceExport,
} from '../../api/PaymentAdviceApi';
import {GridSortModel} from '@mui/x-data-grid';
import {GridPaginationModel} from '@mui/x-data-grid/models/gridPaginationProps';
import PaymentAdviceFilters from './PaymentAdviceFilters';
import {PaymentAdviceDataGrid, PaymentAdviceDetailsDialog} from './PaymentAdviceDataGrid';
import {createFilterChangeHandler, useGridHeight} from "../../utils/DataGridUtils/DataGridUtils";
import {DEFAULT_PAGE_SIZE} from "../../AppConst";
import {csvUtil} from "../../utils/CSV/CSVUtil";
import PaymentAdviceRetryPaymentDialog from "./PaymentAdviceRetryPaymentDialog";
import {getMerchantConfiguration} from "../../api/MerchantsApi";


const PaymentAdviceListComponent: React.FC = () => {
    const [rows, setRows] = useState<PaymentAdviceSummary>({
        entries: [],
        totalElements: 0,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<PaymentAdviceFiltersResponse | undefined>(undefined);
    const [request, setRequest] = useState<PaymentAdvicesRequest>({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        sort: ['transactionDate,desc'],
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {field: 'transactionDate', sort: 'desc'},
    ]);
    const [pageModel, setPageModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
    });
    const [details, setDetails] = useState<PaymentAdviceDetails | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
    const [retryModelDialogOpen, setRetryModelDialogOpen] = useState<boolean>(false);

    const [retryExportStatus, setRetryExportStatus] = useState<Map<string, string>>(new Map());
    const [retryError, setRetryError] = useState<boolean>(false);

    const [selectionModel, setSelectionModel] = useState<string[]>([]);

    const [confirmProceedOpen, setConfirmProceedOpen] = useState<boolean>(false);
    const [blockedEntries, setBlockedEntries] = useState<string[]>([]);

    const filtersRef = useRef<HTMLDivElement | null>(null);
    const gridHeight = useGridHeight(filtersRef);
    const handleFilterChange = createFilterChangeHandler(setRequest);

    useEffect(() => {
        (async () => {
            try {
                const availableFilters = await getPaymentAdviceFilters();
                setFilters(availableFilters);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchPaymentAdvices(request);
            setRows(prev => {
                if (
                    prev.totalElements === data.totalElements &&
                    prev.entries.length === data.entries.length &&
                    prev.entries.every((r, i) => r.id === data.entries[i]?.id)
                ) return prev;
                return data;
            });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRowClick = async (params: any) => {
        const id = params.row.id;
        const det = await getPaymentAdviceDetails(id);
        setDetails(det);
        setDetailsDialogOpen(true);
    };

    const handleDownload = async () => {
        setLoading(true);
        const response = await downloadPaymentAdvices(request);
        await csvUtil(response, setLoading)
    };

    const doRetryExport = async () => {
        setLoading(true);
        try {
            retryPaymentAdviceExport(selectionModel)
                .then(result => {
                    const resultMap = new Map<string, string>();
                    for (const [key, value] of result) {
                        if (value !== "EXPORTED") {
                            setRetryError(true);
                        }
                        const matchingEntry = rows.entries.find(e => e.id === key);
                        if (matchingEntry) {
                            resultMap.set(matchingEntry.pspReference?.trim()?.length ? matchingEntry.pspReference : matchingEntry.id, value);
                        } else {
                            resultMap.set(key, value);
                        }
                    }
                    setRetryExportStatus(resultMap)
                })
                .catch(error => setRetryExportStatus(new Map([["Error", String(error.message).replaceAll('"', '')]])));
            setRetryModelDialogOpen(true);
            await loadData();
            setSelectionModel([]);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleRetryExport = async () => {
        if (!selectionModel || !isRetryAvailable()) return;

        const selectedEntries = rows.entries.filter(pa => selectionModel.includes(pa.id));
        const candidates = selectedEntries.filter(pa => !(pa as any).disbursementReference && !pa.isExternal); // handle null/empty

        try {
            const results = await Promise.all(candidates.map(async (pa) => {
                try {
                    const cfg = await getMerchantConfiguration(pa.merchantAccount, "EXPORT_PA_ONLY_WITH_DISBURSEMENT_REFERENCE");
                    return {pa, cfg};
                } catch (e) {
                    return {pa, cfg: "false"};
                }
            }));

            const blocked = results.filter(r => String(r.cfg).toLowerCase() === "true").map(r => r.pa);

            if (blocked.length > 0) {
                const identifiers = blocked.map(e => (e.pspReference && e.pspReference.trim().length > 0) ? e.pspReference : e.id);
                setBlockedEntries(identifiers);
                setConfirmProceedOpen(true);
                return;
            }

            await doRetryExport();
        } catch (e) {
            console.error(e);
            await doRetryExport();
        }
    };

    const isRetryAvailable = () => {
        if (selectionModel.length === 0) return false;
        return rows.entries.filter(e => selectionModel.includes(e.id)).every(entry =>
            entry.status !== "EXPORTED"
        );
    }

    const onRetryDialogClose = () => {
        setRetryModelDialogOpen(false);
        setRetryError(false);
        setRetryExportStatus(new Map());
    }

    return (
        <Box width="100%">
            <Box ref={filtersRef}>
                <PaymentAdviceFilters filters={filters} request={request} onFilterChange={handleFilterChange}/>
            </Box>

            <Box mt={2}>
                <button type={"button"} className="btn btn-primary-light" onClick={handleDownload} disabled={loading}>
                    Download CSV
                </button>
                {isRetryAvailable() &&
                    <button type={"button"} className="btn btn-primary-light" onClick={handleRetryExport} disabled={loading} style={{marginLeft: '8px'}}>
                        Retry export
                    </button>
                }
                <PaymentAdviceDataGrid
                    rows={rows}
                    loading={loading}
                    sortModel={sortModel}
                    pageModel={pageModel}
                    onSortModelChange={(model) => {
                        setSortModel(model);
                        const sortParam = model.map((s) => `${s.field},${s.sort}`);
                        setRequest(prev => ({
                            ...prev,
                            sort: sortParam,
                            page: 0
                        }));
                    }}
                    onPaginationModelChange={(model) => {
                        setPageModel(model);
                        setRequest(prev => ({
                            ...prev,
                            page: model.page,
                            size: model.pageSize ?? DEFAULT_PAGE_SIZE,
                        }));
                    }}
                    onRowClick={handleRowClick}
                    height={gridHeight}
                    onRowSelectionModelChange={(model) => setSelectionModel(Array.from(model.ids) as string[])}
                />
            </Box>

            <PaymentAdviceDetailsDialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} details={details}/>
            <PaymentAdviceRetryPaymentDialog open={retryModelDialogOpen}
                                             onClose={onRetryDialogClose}
                                             retryError={retryError}
                                             retryExportStatus={retryExportStatus}/>

            <Dialog open={confirmProceedOpen} onClose={() => setConfirmProceedOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent dividers>
                    <Box>
                        <p>
                            For the selected entries listed below, the disbursement reference is null, and the merchant configuration
                            is set to not export payment advices without a disbursement reference. Do you still want to proceed?
                        </p>
                        {blockedEntries.length > 0 && (
                            <Box sx={{mt: 1}}>
                                {blockedEntries.map(id => (
                                    <div key={id}><strong>{id}</strong></div>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setConfirmProceedOpen(false);
                        setBlockedEntries([]);
                    }}>Cancel</Button>
                    <Button onClick={async () => {
                        setConfirmProceedOpen(false);
                        setBlockedEntries([]);
                        await doRetryExport();
                    }} variant="contained" color="primary">Proceed</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentAdviceListComponent;
